import React from "react";
import AlertModal from "shared/components/alertModal";
import { AlertModalHandleConfirmArgsType, AlertModalType } from "shared/components/alertModal/type";
import { ConfirmationAlertHookService } from "./types";

export default function useConfirmationAlert(props?: AlertModalType): ConfirmationAlertHookService {
  const [confirmationAlertProps, setConfirmationAlertProps] = React.useState<AlertModalType | null>();
  const initConfirmationAlert = React.useCallback(
    (initProps: AlertModalType | null) => {
      if (initProps === null) {
        setConfirmationAlertProps(null);
      } else {
        setConfirmationAlertProps({
          ...props,
          ...initProps
        });
      }
    },
    [props]
  );

  const onConfirmHandler = React.useCallback(
    async (args: AlertModalHandleConfirmArgsType) => {
      try {
        const result = await confirmationAlertProps?.handleConfirm?.(args);
        if (result !== false) {
          initConfirmationAlert(null);
          return true;
        }
      } catch (err) {}
    },
    [confirmationAlertProps, initConfirmationAlert]
  );

  const closeHandler = React.useCallback(
    async event => {
      try {
        await confirmationAlertProps?.handleClose?.(event);
      } catch (err) {}
      initConfirmationAlert(null);
      return true;
    },
    [confirmationAlertProps, initConfirmationAlert]
  );

  const dismissHandler = React.useCallback(() => {
    initConfirmationAlert(null);
  }, [initConfirmationAlert]);

  const alertComponent = React.useMemo(() => {
    return confirmationAlertProps ? (
      <AlertModal
        handleConfirm={onConfirmHandler}
        message={confirmationAlertProps?.message}
        handleClose={closeHandler}
        className={confirmationAlertProps?.className}
        title={confirmationAlertProps?.title}
        additionalConfirmation={confirmationAlertProps?.additionalConfirmation}
        additionalConfirmationOptional={confirmationAlertProps?.additionalConfirmationOptional}
        additionalConfirmationMessage={confirmationAlertProps?.additionalConfirmationMessage}
        primaryButtonIsOutlined={confirmationAlertProps?.primaryButtonIsOutlined}
        secondaryButtonIsOutlined={confirmationAlertProps?.secondaryButtonIsOutlined}
        primaryButtonText={confirmationAlertProps?.primaryButtonText}
        primaryButtonVariant={confirmationAlertProps?.primaryButtonVariant}
        secondaryButtonVariant={confirmationAlertProps?.secondaryButtonVariant}
        secondaryButtonText={confirmationAlertProps?.secondaryButtonText}
        hideSecondaryButton={!!props?.hideSecondaryButton || !!confirmationAlertProps?.hideSecondaryButton}
        primaryButtonIcon={confirmationAlertProps?.primaryButtonIcon}
        secondaryButtonIcon={confirmationAlertProps?.secondaryButtonIcon}
        handleDismiss={confirmationAlertProps?.showDismissButton ? dismissHandler : undefined}
      />
    ) : null;
  }, [closeHandler, confirmationAlertProps, dismissHandler, onConfirmHandler, props?.hideSecondaryButton]);

  return {
    alertComponent,
    confirmationAlertProps,
    initConfirmationAlert
  };
}
